import { connectorFactory } from '@/accessors/common/connectorFactory'
import type {
  AccessDataBasedOnNetwork,
  ParamType,
  ResType
} from '@/types/accessor/common/accessDataBasedOnNetwork'
import type { ApiName } from '@/types/accessor/api/common/apiName'
import { isOnlineByHealthChecker } from '../../modules/healthCheck/healthChecker'
import { ERROR_CODE } from '@/modules/miiaFw/MiiaFwCognito'

/**
 * オフライン・オンラインの状態によって取得先を変えるための関数
 * @param apiName apiの名前
 * @param apiParam apiに渡すparam
 * @param isOnlineByStore オンラインかどうか
 * @param offlineCallback StoreでオンラインだがAPI実行でオフラインであると判明した際に呼び出されるコールバック関数
 * @return apiで取得できるデータが返される。
 */
export const accessDataBasedOnNetwork: AccessDataBasedOnNetwork = async <T extends ApiName>(
  apiName: T,
  apiParam: ParamType<T>,
  isOnlineByStore: boolean,
  offlineCallback: () => void
): Promise<ResType<T> | undefined> => {
  const connector = connectorFactory(apiName)
  if (connector === undefined) return undefined
  if (isOnlineByStore && (await isOnlineByHealthChecker())) {
    try {
      // オンライン処理
      return (await connector.api(apiParam, connector.indexedDB.forApi)) as ResType<T>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      // エラーが共通化されていないため、確認できたエラーをここで追加する
      // エラーコードがまれにnumber型の場合があるため、ここでstring型に変換する
      // TODO: エラー共通化が行われた際にerrorCodeの記述を一本化する
      const errorCode = String(
        (err => {
          if (err.errorCode !== undefined) return err.errorCode
          if (err.message.errorCode !== undefined) return err.message.errorCode
          if (err.message.code !== undefined) return err.message.code
          return undefined
        })(e)
      )

      // タイムアウトの際はオフラインとして判定し、下の処理に移る
      if (errorCode !== String(ERROR_CODE.CLIENT_REQUEST_TIMEOUT)) throw e
    }
  }
  // オフラインにもかかわらずStoreのデータがOnlineだった場合、offlineCallbackを実行
  if (isOnlineByStore) offlineCallback()
  // オフライン処理
  return (await connector.indexedDB.forMain(apiParam)) as ResType<T>
}
